import { AssistantBlock, AssistantMessage, AssistantPendingAction, segmentsOf } from '@/cloud/models/assistant.models';
import { AssistantMarkdownPipe } from './assistant-markdown.pipe';

/** Export only conversation content, not tool activity, suggested prompts or interactive controls. */
export function conversationTranscript(messages: AssistantMessage[], title: string, labels: { user: string; assistant: string }, actions: Record<string, AssistantPendingAction> = {}): { html: string; text: string } {
    const root = document.createElement('div');
    const markdown = new AssistantMarkdownPipe();
    const appendText = (parent: HTMLElement, tag: string, text: string) => {
        const element = document.createElement(tag);
        element.textContent = text;
        parent.append(element);
        return element;
    };
    appendText(root, 'h3', title);
    for (const message of messages) {
        const section = document.createElement('section');
        appendText(section, 'h4', message.role === 'user' ? labels.user : labels.assistant);
        for (const segment of segmentsOf(message.items)) {
            if (segment.kind === 'text' && message.role === 'assistant') {
                const answer = document.createElement('div');
                answer.innerHTML = markdown.transform(segment.text);
                section.append(answer);
            } else if (segment.kind === 'text' || segment.kind === 'error') {
                appendText(section, 'p', segment.text).style.whiteSpace = 'pre-wrap';
            } else if (segment.kind === 'block') {
                const block = segment.block;
                appendText(section, 'pre', blockText(block.type === 'action' ? { ...block, action: actions[block.action.id] ?? block.action } : block)).style.whiteSpace = 'pre-wrap';
            }
        }
        if (section.children.length > 1) root.append(section);
    }
    return {
        html: root.innerHTML,
        text: plainText(root)
            .replace(/\n{3,}/g, '\n\n')
            .trim()
    };
}

function blockText(block: AssistantBlock): string {
    switch (block.type) {
        case 'devices':
            return [block.title, ...block.devices.map((d) => [d.name, d.namespaceName, d.locationName, d.model, d.offline ? 'Offline' : d.status, d.ipAddress, d.firmwareVersion, d.lastCheckInUtc].filter(Boolean).join(' · '))]
                .filter(Boolean)
                .join('\n');
        case 'rooms':
            return [block.title, ...block.rooms.map((r) => `${[r.name, r.namespaceName].filter(Boolean).join(' · ')}: ${r.devices} devices, ${r.offline} offline; ${r.rating ?? '—'}/5 (${r.ratingCount} ratings)`)].filter(Boolean).join('\n');
        case 'chart':
            return [[block.title, block.unit].filter(Boolean).join(' · '), ['', ...block.series.map((s) => s.name)].join('\t'), ...block.labels.map((label, i) => [label, ...block.series.map((s) => s.values[i] ?? '—')].join('\t'))].join('\n');
        case 'logs':
            return [block.title, block.deviceName, ...block.entries.map((e) => [e.timestampUtc, e.level, e.message].filter(Boolean).join(' · '))].filter(Boolean).join('\n');
        case 'timeline':
            return [block.title, ...block.events.map((e) => [e.timeUtc, e.label].filter(Boolean).join(' · '))].filter(Boolean).join('\n');
        case 'action':
            const status = block.action.status === 'Proposed' && Date.parse(block.action.expiresUtc) <= Date.now() ? 'Expired' : block.action.status;
            return `${block.action.action} · ${block.action.deviceName} · ${status}\n${block.action.reason}${block.action.error ? `\n${block.action.error}` : ''}`;
        case 'issueProposal': {
            const p = block.proposal;
            const state = p.status === 'Proposed' && Date.parse(p.expiresUtc) <= Date.now() ? 'Expired' : p.status;
            const what =
                p.kind === 'Comment'
                    ? `Comment on ${p.repository}#${p.issueNumber}`
                    : p.kind === 'Update'
                      ? `${p.newState === 'open' ? 'Reopen' : p.newState === 'closed' ? 'Close' : 'Update'} ${p.repository}#${p.issueNumber}`
                      : `GitHub issue in ${p.repository}: ${p.title}`;
            const result = p.resultUrl ? `\n${p.resultUrl}` : '';
            return `${what} · ${state}\n${p.reason}${result}${p.error ? `\n${p.error}` : ''}`;
        }
        case 'issues':
            return [block.title, ...block.issues.map((i) => `${i.repository}#${i.number} · ${i.title} · ${i.state} · ${i.htmlUrl}`)].filter(Boolean).join('\n');
    }
}

/** Keep paragraph/list/table boundaries and link destinations when pasting into plain-text editors. */
function plainText(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        return !text.trim() && text.includes('\n') && !node.parentElement?.closest('pre') ? '' : text;
    }
    const children = Array.from(node.childNodes).map(plainText).join('');
    if (!(node instanceof HTMLElement)) return children;
    switch (node.tagName) {
        case 'BR':
            return '\n';
        case 'HR':
            return '\n\n';
        case 'LI': {
            const marker = node.parentElement?.tagName === 'OL' ? `${Array.from(node.parentElement.children).indexOf(node) + 1}.` : '•';
            return `${marker} ${children.trim()}\n`;
        }
        case 'TH':
        case 'TD':
            return `${children}\t`;
        case 'TR':
            return `${children.trimEnd()}\n`;
        case 'A': {
            const href = node.getAttribute('href');
            return href && href !== children ? `${children} (${href})` : children;
        }
        case 'P':
        case 'DIV':
        case 'SECTION':
        case 'H3':
        case 'H4':
        case 'PRE':
        case 'BLOCKQUOTE':
        case 'UL':
        case 'OL':
        case 'TABLE':
            return `${children}\n\n`;
        default:
            return children;
    }
}
